import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';

export type DsTabsOrientation = 'horizontal' | 'vertical';
export type DsTabsSize = 's' | 'm' | 'l';
export type DsTabsResponsiveMode = 'scroll' | 'dropdown';

let nextTabsDropdownId = 0;

export interface DsTabOption {
  label: string;
  value: string;
  icon?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

@Component({
  selector: 'ds-tabs',
  standalone: true,
  imports: [DsIconButtonV2Component, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (tabs.length > 0) {
      <div class="ds-tabs-layout" [class.ds-tabs-layout--dropdown]="responsiveMode === 'dropdown'">
        <div
          class="ds-tabs"
          [class.ds-tabs--disabled]="disabled"
          [class.ds-tabs--horizontal]="orientation === 'horizontal'"
          [class.ds-tabs--vertical]="orientation === 'vertical'"
          [class.ds-tabs--s]="size === 's'"
          [class.ds-tabs--m]="size === 'm'"
          [class.ds-tabs--l]="size === 'l'"
          [class.ds-tabs--compact]="isCompact"
          [style.--ds-tabs-count]="tabs.length"
          [style.--ds-tabs-selected-index]="selectedIndex"
          [style.--ds-tabs-indicator-basis]="indicatorBasis"
        >
        @if (orientation === 'horizontal') {
          <div class="ds-tabs__scroll-affordance ds-tabs__scroll-affordance--start" [class.ds-tabs__scroll-affordance--visible]="canScrollStart">
            <ds-icon-button-v2
              icon="KeyboardArrowLeft"
              size="xs"
              emphasis="terciary"
              [disabled]="!canScrollStart"
              [ariaLabel]="scrollPrevAriaLabel"
              (clicked)="scrollByStep(-1)"
            />
          </div>
        }

        <div class="ds-tabs__viewport">
          <div
            class="ds-tabs__track"
            #track
            role="tablist"
            [attr.aria-disabled]="disabled || null"
            [attr.aria-orientation]="orientation"
            (scroll)="onTrackScroll()"
          >
            <span class="ds-tabs__indicator" [style.transform]="indicatorTransform" aria-hidden="true"></span>

            @for (tab of tabs; track tab.value; let index = $index) {
              <button
                class="ds-tabs__tab"
                type="button"
                role="tab"
                [class.ds-tabs__tab--selected]="index === selectedIndex"
                [disabled]="disabled || tab.disabled || null"
                [attr.aria-selected]="index === selectedIndex"
                [attr.aria-label]="tab.ariaLabel || null"
                [attr.tabindex]="!disabled && !tab.disabled ? 0 : -1"
                (click)="selectAt(index)"
                (keydown)="onKeydown($event, index)"
              >
                @if (tab.icon) {
                  <ds-icon [name]="tab.icon" [size]="'var(--_icon-size)'" />
                }
                @if (tab.label) {
                  <span class="ds-tabs__label">{{ tab.label }}</span>
                }
              </button>
            }
          </div>
        </div>

        @if (orientation === 'horizontal') {
          <div class="ds-tabs__scroll-affordance ds-tabs__scroll-affordance--end" [class.ds-tabs__scroll-affordance--visible]="canScrollEnd">
            <ds-icon-button-v2
              icon="KeyboardArrowRight"
              size="xs"
              emphasis="terciary"
              [disabled]="!canScrollEnd"
              [ariaLabel]="scrollNextAriaLabel"
              (clicked)="scrollByStep(1)"
            />
          </div>
        }
        </div>

        @if (responsiveMode === 'dropdown') {
          <div class="ds-tabs__dropdown" [class.ds-tabs__dropdown--disabled]="disabled">
            @if (selectedTab; as currentTab) {
              <button
                #dropdownTrigger
                class="ds-tabs__dropdown-trigger"
                type="button"
                [disabled]="disabled || null"
                aria-haspopup="listbox"
                [attr.aria-expanded]="dropdownOpen"
                [attr.aria-controls]="dropdownListId"
                [attr.aria-label]="dropdownAriaLabel + ': ' + (currentTab.ariaLabel || currentTab.label)"
                (click)="toggleDropdown()"
                (keydown)="onDropdownTriggerKeydown($event)"
              >
                <span class="ds-tabs__dropdown-current" aria-hidden="true">
                  @if (currentTab.icon) {
                    <ds-icon [name]="currentTab.icon" [size]="'var(--ds-tabs-s-icon-size)'" />
                  }
                  @if (currentTab.label) {
                    <span class="ds-tabs__dropdown-label">{{ currentTab.label }}</span>
                  }
                </span>
                <span class="ds-tabs__dropdown-action" aria-hidden="true">
                  <ds-icon
                    name="KeyboardArrowDown"
                    [size]="'var(--ds-tabs-dropdown-chevron-icon-size)'"
                    [class.ds-tabs__dropdown-chevron--open]="dropdownOpen"
                  />
                </span>
              </button>
            }

            @if (dropdownOpen) {
              <div class="ds-tabs__dropdown-menu" [id]="dropdownListId" role="listbox">
                @for (tab of tabs; track tab.value; let index = $index) {
                  <button
                    class="ds-tabs__dropdown-option"
                    type="button"
                    role="option"
                    [class.ds-tabs__dropdown-option--selected]="index === selectedIndex"
                    [disabled]="disabled || tab.disabled || null"
                    [attr.aria-selected]="index === selectedIndex"
                    [attr.aria-label]="tab.ariaLabel || null"
                    (click)="selectDropdownAt(index)"
                    (keydown)="onDropdownOptionKeydown($event, index)"
                  >
                    @if (tab.icon) {
                      <ds-icon [name]="tab.icon" [size]="'var(--ds-tabs-s-icon-size)'" />
                    }
                    @if (tab.label) {
                      <span class="ds-tabs__dropdown-label">{{ tab.label }}</span>
                    }
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      max-width: 100%;
      overflow: visible;
    }

    .ds-tabs-layout {
      position: relative;
      display: block;
      width: 100%;
      max-width: 100%;
    }

    .ds-tabs__dropdown {
      position: relative;
      display: none;
      width: 100%;
      font-family: 'Sura Sans', system-ui, sans-serif;
    }

    .ds-tabs__dropdown--disabled {
      opacity: calc(var(--ds-tabs-disabled-opacity) / 100px);
      pointer-events: none;
    }

    .ds-tabs__dropdown-trigger {
      display: flex;
      width: 100%;
      height: var(--ds-tabs-dropdown-height);
      align-items: center;
      gap: var(--ds-tabs-s-item-gap);
      padding: var(--ds-tabs-container-padding);
      border: 0;
      border-radius: var(--ds-tabs-container-radius);
      background: var(--ds-tabs-background);
      box-sizing: border-box;
      color: var(--ds-tabs-selected-content-color);
      cursor: pointer;
      outline: none;
    }

    .ds-tabs__dropdown-trigger:focus-visible {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-tabs__dropdown-current,
    .ds-tabs__dropdown-action {
      display: flex;
      height: 100%;
      align-items: center;
      justify-content: center;
      border-radius: var(--ds-tabs-selected-radius);
      background: var(--ds-tabs-selected-background);
      box-sizing: border-box;
    }

    .ds-tabs__dropdown-current {
      min-width: 0;
      flex: 1 1 0;
      gap: var(--ds-tabs-dropdown-item-gap);
      padding-inline: var(--ds-tabs-s-item-padding-x);
    }

    .ds-tabs__dropdown-action {
      width: var(--ds-tabs-dropdown-action-width);
      flex: 0 0 var(--ds-tabs-dropdown-action-width);
    }

    .ds-tabs__dropdown-action ds-icon {
      transition: transform 180ms ease;
    }

    .ds-tabs__dropdown-chevron--open {
      transform: rotate(180deg);
    }

    .ds-tabs__dropdown-label {
      display: block;
      min-width: 0;
      overflow: hidden;
      color: inherit;
      font-size: var(--ds-tabs-s-font-size);
      font-weight: var(--ds-tabs-font-weight);
      line-height: var(--ds-tabs-s-line-height);
      text-box-trim: trim-both;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-tabs__dropdown-menu {
      position: absolute;
      top: calc(100% + var(--ds-tabs-dropdown-menu-gap));
      right: 0;
      left: 0;
      z-index: var(--ds-tabs-dropdown-z-index);
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: var(--ds-tabs-s-item-gap);
      padding: var(--ds-tabs-container-padding);
      border-radius: var(--ds-tabs-container-radius);
      background: var(--ds-tabs-dropdown-menu-background);
      box-shadow: var(--ds-tabs-dropdown-menu-shadow);
      box-sizing: border-box;
    }

    .ds-tabs__dropdown-option {
      position: relative;
      display: flex;
      width: 100%;
      min-height: var(--ds-tabs-dropdown-option-min-height);
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      gap: var(--ds-tabs-dropdown-item-gap);
      padding: 0 var(--ds-tabs-s-item-padding-x);
      border: 0;
      border-radius: var(--ds-tabs-selected-radius);
      background: var(--ds-tabs-dropdown-menu-background);
      box-sizing: border-box;
      color: var(--ds-tabs-selected-content-color);
      cursor: pointer;
      font-family: inherit;
      outline: none;
    }

    .ds-tabs__dropdown-option:hover:not(:disabled) {
      background: var(--ds-tabs-hover-overlay);
    }

    .ds-tabs__dropdown-option:focus-visible {
      box-shadow: inset 0 0 0 var(--ds-focus-stroke) var(--ds-focus-color);
    }

    .ds-tabs__dropdown-option:disabled {
      color: var(--ds-tabs-disabled-content-color);
      cursor: not-allowed;
      opacity: calc(var(--ds-tabs-disabled-opacity) / 100px);
    }

    .ds-tabs {
      --_container-padding: var(--ds-tabs-container-padding);
      --_height: var(--ds-tabs-l-height);
      --_min-item-width: var(--ds-tabs-l-min-item-width);
      --_item-gap: var(--ds-tabs-l-item-gap);
      --_item-padding-x: var(--ds-tabs-l-item-padding-x);
      --_font-size: var(--ds-tabs-l-font-size);
      --_line-height: var(--ds-tabs-l-line-height);
      --_icon-size: var(--ds-tabs-l-icon-size);
      position: relative;
      display: block;
      box-sizing: border-box;
      isolation: isolate;
    }

    .ds-tabs__viewport {
      min-width: 0;
      padding: var(--_container-padding);
      border-radius: var(--ds-tabs-container-radius);
      background: var(--ds-tabs-background);
      box-sizing: border-box;
      overflow: hidden;
    }

    .ds-tabs--s {
      --_height: var(--ds-tabs-s-height);
      --_min-item-width: var(--ds-tabs-s-min-item-width);
      --_item-gap: var(--ds-tabs-s-item-gap);
      --_item-padding-x: var(--ds-tabs-s-item-padding-x);
      --_font-size: var(--ds-tabs-s-font-size);
      --_line-height: var(--ds-tabs-s-line-height);
      --_icon-size: var(--ds-tabs-s-icon-size);
    }

    .ds-tabs--m {
      --_height: var(--ds-tabs-m-height);
      --_min-item-width: var(--ds-tabs-m-min-item-width);
      --_item-gap: var(--ds-tabs-m-item-gap);
      --_item-padding-x: var(--ds-tabs-m-item-padding-x);
      --_font-size: var(--ds-tabs-m-font-size);
      --_line-height: var(--ds-tabs-m-line-height);
      --_icon-size: var(--ds-tabs-m-icon-size);
    }

    .ds-tabs--l {
      --_height: var(--ds-tabs-l-height);
      --_min-item-width: var(--ds-tabs-l-min-item-width);
      --_item-gap: var(--ds-tabs-l-item-gap);
      --_item-padding-x: var(--ds-tabs-l-item-padding-x);
      --_font-size: var(--ds-tabs-l-font-size);
      --_line-height: var(--ds-tabs-l-line-height);
      --_icon-size: var(--ds-tabs-l-icon-size);
    }

    /* Foundation mobile breakpoint: M and L collapse to the S specification. */
    @media (max-width: 839px) {
      .ds-tabs-layout--dropdown .ds-tabs {
        display: none;
      }

      .ds-tabs-layout--dropdown .ds-tabs__dropdown {
        display: block;
      }

      .ds-tabs--m,
      .ds-tabs--l {
        --_height: var(--ds-tabs-s-height);
        --_min-item-width: var(--ds-tabs-s-min-item-width);
        --_item-gap: var(--ds-tabs-s-item-gap);
        --_item-padding-x: var(--ds-tabs-s-item-padding-x);
        --_font-size: var(--ds-tabs-s-font-size);
        --_line-height: var(--ds-tabs-s-line-height);
        --_icon-size: var(--ds-tabs-s-icon-size);
      }
    }

    .ds-tabs--horizontal {
      width: 100%;
      height: calc(var(--_height) + var(--_container-padding) + var(--_container-padding));
    }

    .ds-tabs--horizontal .ds-tabs__viewport {
      width: 100%;
      height: 100%;
    }

    .ds-tabs--vertical {
      width: var(--ds-tabs-vertical-width);
      min-width: var(--ds-tabs-vertical-width);
    }

    .ds-tabs--vertical .ds-tabs__viewport {
      width: 100%;
    }

    .ds-tabs--disabled {
      opacity: calc(var(--ds-tabs-disabled-opacity) / 100px);
      pointer-events: none;
    }

    .ds-tabs__track {
      position: relative;
      display: grid;
      height: 100%;
    }

    .ds-tabs--horizontal .ds-tabs__track {
      grid-template-columns: repeat(var(--ds-tabs-count), 1fr);
    }

    .ds-tabs--vertical .ds-tabs__track {
      grid-template-rows: repeat(var(--ds-tabs-count), var(--_height));
    }

    .ds-tabs__indicator {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      border-radius: var(--ds-tabs-selected-radius);
      background: var(--ds-tabs-selected-background);
      box-shadow: var(--ds-tabs-selected-shadow);
      transition:
        transform 180ms ease,
        width 180ms ease,
        height 180ms ease,
        background-color 120ms ease;
      pointer-events: none;
    }

    .ds-tabs--horizontal .ds-tabs__indicator {
      width: var(--ds-tabs-indicator-basis);
      height: 100%;
    }

    .ds-tabs--vertical .ds-tabs__indicator {
      width: 100%;
      height: var(--_height);
    }

    .ds-tabs__tab {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--_item-gap);
      min-width: 0;
      height: 100%;
      padding: 0 var(--_item-padding-x);
      border: 0;
      border-radius: var(--ds-tabs-selected-radius);
      background: transparent;
      box-sizing: border-box;
      color: var(--ds-tabs-content-color);
      cursor: pointer;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--_font-size);
      font-weight: var(--ds-tabs-font-weight);
      line-height: var(--_line-height);
      outline: none;
      overflow: hidden;
      transition:
        color 120ms ease,
        background-color 120ms ease,
        box-shadow 120ms ease;
    }

    .ds-tabs--vertical .ds-tabs__tab {
      justify-content: flex-start;
      height: var(--_height);
    }

    .ds-tabs__tab::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: var(--ds-tabs-hover-overlay);
      opacity: 0;
      transition: opacity 120ms ease;
      pointer-events: none;
    }

    .ds-tabs__tab::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      box-shadow: inset 0 0 0 var(--ds-focus-stroke) var(--ds-focus-color);
      opacity: 0;
      pointer-events: none;
    }

    .ds-tabs__tab:hover:not(:disabled):not(.ds-tabs__tab--selected)::before {
      opacity: 1;
    }

    .ds-tabs__tab--selected {
      color: var(--ds-tabs-selected-content-color);
    }

    .ds-tabs__tab:disabled {
      color: var(--ds-tabs-disabled-content-color);
      cursor: not-allowed;
      opacity: calc(var(--ds-tabs-disabled-opacity) / 100px);
    }

    .ds-tabs__tab:focus-visible::after {
      opacity: 1;
    }

    .ds-tabs__label,
    ds-icon {
      position: relative;
      z-index: 1;
      flex: 0 1 auto;
    }

    .ds-tabs__label {
      display: block;
      min-width: 0;
      overflow: visible;
      text-box-edge: cap alphabetic;
      text-box-trim: trim-both;
      white-space: nowrap;
    }

    .ds-tabs__scroll-affordance {
      display: none;
      width: var(--ds-tabs-scroll-button-width);
      min-width: 0;
      height: 100%;
      opacity: 0;
      pointer-events: none;
      transition: opacity 120ms ease;
    }

    .ds-tabs__scroll-affordance--start {
      grid-column: 1;
      grid-row: 1;
    }

    .ds-tabs__scroll-affordance--end {
      grid-column: 3;
      grid-row: 1;
    }

    .ds-tabs__scroll-affordance--visible {
      opacity: 1;
      pointer-events: auto;
    }

    /*
     * Compact mode is driven by measured overflow (see updateLayoutMode), not
     * by viewport width: a couple of short tabs should stay centered in the
     * equal-width grid even on a narrow screen, and only switch to a
     * left-aligned, scrollable list once the labels would actually be
     * clipped by their column.
     */
    .ds-tabs--horizontal.ds-tabs--compact {
      display: grid;
      grid-template-columns:
        var(--ds-tabs-scroll-button-width)
        minmax(0, 1fr)
        var(--ds-tabs-scroll-button-width);
    }

    .ds-tabs--horizontal.ds-tabs--compact .ds-tabs__viewport {
      grid-column: 2;
      grid-row: 1;
      padding: 0;
    }

    .ds-tabs--horizontal.ds-tabs--compact .ds-tabs__track {
      display: flex;
      min-width: 0;
      padding: var(--_container-padding);
      box-sizing: border-box;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-padding-inline: var(--_container-padding);
      scroll-behavior: smooth;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
      touch-action: pan-x;
      overscroll-behavior-inline: contain;
    }

    .ds-tabs--horizontal.ds-tabs--compact .ds-tabs__track::-webkit-scrollbar {
      display: none;
    }

    .ds-tabs--horizontal.ds-tabs--compact .ds-tabs__tab {
      flex: 0 0 auto;
      width: auto;
      min-width: var(--_min-item-width);
    }

    .ds-tabs--horizontal.ds-tabs--compact .ds-tabs__indicator {
      display: none;
    }

    .ds-tabs--horizontal.ds-tabs--compact .ds-tabs__tab--selected {
      background: var(--ds-tabs-selected-background);
      box-shadow: var(--ds-tabs-selected-shadow);
    }

    .ds-tabs--horizontal.ds-tabs--compact .ds-tabs__scroll-affordance {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `],
})
export class DsTabsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() tabs: DsTabOption[] = [];
  @Input() value = '';
  @Input() disabled = false;
  @Input() orientation: DsTabsOrientation = 'horizontal';
  @Input() size: DsTabsSize = 'l';
  @Input() responsiveMode: DsTabsResponsiveMode = 'scroll';
  @Input() scrollPrevAriaLabel = 'Ver pestañas anteriores';
  @Input() scrollNextAriaLabel = 'Ver más pestañas';
  @Input() dropdownAriaLabel = 'Seleccionar pestaña';

  @Output() valueChange = new EventEmitter<string>();
  @Output() tabSelected = new EventEmitter<DsTabOption>();

  @ViewChild('track') private trackRef?: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownTrigger') private dropdownTriggerRef?: ElementRef<HTMLButtonElement>;

  canScrollStart = false;
  canScrollEnd = false;
  isCompact = false;
  dropdownOpen = false;

  readonly dropdownListId = `ds-tabs-dropdown-${++nextTabsDropdownId}`;

  private resizeObserver?: ResizeObserver;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly hostRef: ElementRef<HTMLElement>,
  ) {}

  get selectedIndex(): number {
    const index = this.tabs.findIndex(tab => tab.value === this.value);
    return Math.max(index, 0);
  }

  get selectedTab(): DsTabOption | undefined {
    return this.tabs[this.selectedIndex];
  }

  get indicatorBasis(): string {
    return this.tabs.length ? `${100 / this.tabs.length}%` : '0%';
  }

  get indicatorTransform(): string {
    const amount = this.selectedIndex * 100;
    return this.orientation === 'vertical'
      ? `translateY(${amount}%)`
      : `translateX(${amount}%)`;
  }

  ngAfterViewInit(): void {
    this.observeTrack();
    this.updateLayoutMode();
    this.updateScrollAffordance();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['responsiveMode'] && this.responsiveMode !== 'dropdown') {
      this.dropdownOpen = false;
    }
    if (changes['tabs'] || changes['orientation']) {
      this.runAfterRender(() => {
        this.updateLayoutMode();
        this.updateScrollAffordance();
      });
    }
    if (changes['value'] && !changes['value'].isFirstChange()) {
      this.runAfterRender(() => this.scrollTabIntoView(this.selectedIndex));
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  onTrackScroll(): void {
    this.updateScrollAffordance();
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.runAfterRender(() => this.focusDropdownOption(this.selectedIndex));
    }
  }

  selectDropdownAt(index: number): void {
    const tab = this.tabs[index];
    if (!tab || this.disabled || tab.disabled) return;
    this.selectAt(index);
    this.dropdownOpen = false;
    this.runAfterRender(() => this.dropdownTriggerRef?.nativeElement.focus());
  }

  onDropdownTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.dropdownOpen) {
      event.preventDefault();
      this.dropdownOpen = false;
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    this.dropdownOpen = true;
    this.runAfterRender(() => this.focusDropdownOption(this.selectedIndex));
  }

  onDropdownOptionKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown(true);
      return;
    }
    if (event.key === 'Tab') {
      this.dropdownOpen = false;
      return;
    }

    const keyMap: Record<string, number | 'first' | 'last'> = {
      ArrowDown: 1,
      ArrowRight: 1,
      ArrowUp: -1,
      ArrowLeft: -1,
      Home: 'first',
      End: 'last',
    };
    const direction = keyMap[event.key];
    if (direction === undefined) return;

    event.preventDefault();
    const nextIndex = this.resolveNextIndex(index, direction);
    if (nextIndex !== -1) this.focusDropdownOption(nextIndex);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.dropdownOpen) return;
    const target = event.target;
    if (target instanceof Node && this.hostRef.nativeElement.contains(target)) return;
    this.dropdownOpen = false;
    this.changeDetector.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.dropdownOpen) this.closeDropdown(true);
  }

  scrollByStep(direction: 1 | -1): void {
    const track = this.trackRef?.nativeElement;
    if (!track) return;

    const buttons = Array.from(track.querySelectorAll<HTMLButtonElement>('.ds-tabs__tab'));
    if (buttons.length === 0) return;

    const trackBounds = track.getBoundingClientRect();
    const scrollPadding = Number.parseFloat(
      getComputedStyle(track).scrollPaddingInlineStart,
    ) || 0;
    const contentStart = trackBounds.left + scrollPadding;

    let leadingIndex = 0;
    for (let index = 0; index < buttons.length; index += 1) {
      if (buttons[index].getBoundingClientRect().left > contentStart + 1) break;
      leadingIndex = index;
    }

    const leadingLeft = buttons[leadingIndex].getBoundingClientRect().left;
    const leadingIsAligned = Math.abs(leadingLeft - contentStart) <= 1;
    const targetIndex = direction === 1
      ? Math.min(leadingIndex + 1, buttons.length - 1)
      : Math.max(leadingIndex - (leadingIsAligned ? 1 : 0), 0);
    const targetLeft = buttons[targetIndex].getBoundingClientRect().left;
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const left = Math.min(
      maximum,
      Math.max(0, track.scrollLeft + targetLeft - contentStart),
    );

    track.scrollTo({ left, behavior: 'smooth' });
  }

  selectAt(index: number): void {
    const tab = this.tabs[index];
    if (!tab || this.disabled || tab.disabled) return;
    this.value = tab.value;
    this.valueChange.emit(tab.value);
    this.tabSelected.emit(tab);
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const keyMap: Record<string, number | 'first' | 'last'> = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
      Home: 'first',
      End: 'last',
    };
    const direction = keyMap[event.key];
    if (direction === undefined) return;

    event.preventDefault();
    const nextIndex = this.resolveNextIndex(index, direction);
    if (nextIndex === -1) return;
    this.selectAt(nextIndex);
    this.focusTab(event.currentTarget, nextIndex);
  }

  private resolveNextIndex(
    currentIndex: number,
    direction: number | 'first' | 'last',
  ): number {
    if (direction === 'first') return this.tabs.findIndex(tab => !tab.disabled);
    if (direction === 'last') {
      for (let i = this.tabs.length - 1; i >= 0; i -= 1) {
        if (!this.tabs[i]?.disabled) return i;
      }
      return -1;
    }

    for (let step = 1; step <= this.tabs.length; step += 1) {
      const nextIndex = (currentIndex + direction * step + this.tabs.length) % this.tabs.length;
      if (!this.tabs[nextIndex]?.disabled) return nextIndex;
    }
    return -1;
  }

  private focusTab(currentTarget: EventTarget | null, index: number): void {
    const host = currentTarget instanceof HTMLElement ? currentTarget.parentElement : null;
    const button = host?.querySelectorAll<HTMLButtonElement>('.ds-tabs__tab')[index];
    window.queueMicrotask(() => {
      button?.focus();
      button?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  private focusDropdownOption(index: number): void {
    const options = this.hostRef.nativeElement.querySelectorAll<HTMLButtonElement>(
      '.ds-tabs__dropdown-option',
    );
    const requested = options[index];
    if (requested && !requested.disabled) {
      requested.focus();
      return;
    }
    Array.from(options).find(option => !option.disabled)?.focus();
  }

  private closeDropdown(restoreFocus: boolean): void {
    this.dropdownOpen = false;
    this.changeDetector.markForCheck();
    if (restoreFocus) {
      this.runAfterRender(() => this.dropdownTriggerRef?.nativeElement.focus());
    }
  }

  private scrollTabIntoView(index: number): void {
    const button = this.trackRef?.nativeElement.querySelectorAll<HTMLButtonElement>('.ds-tabs__tab')[index];
    button?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  private observeTrack(): void {
    const track = this.trackRef?.nativeElement;
    if (!track || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      this.updateLayoutMode();
      this.updateScrollAffordance();
    });
    this.resizeObserver.observe(track);
  }

  private runAfterRender(callback: () => void): void {
    this.ngZone.runOutsideAngular(() => window.queueMicrotask(callback));
  }

  /**
   * Horizontal tabs default to an equal-width grid. Compact mode (left-aligned,
   * content-sized, scrollable) only kicks in once that grid would actually
   * clip a label — never just because the viewport is narrow. While compact,
   * tabs are already content-sized, so their own rendered width doubles as
   * the "would it fit in the grid" measurement for switching back.
   */
  private updateLayoutMode(): void {
    if (this.orientation !== 'horizontal') return;
    const track = this.trackRef?.nativeElement;
    if (!track || !track.clientWidth) return;

    const buttons = Array.from(track.querySelectorAll<HTMLButtonElement>('.ds-tabs__tab'));
    if (buttons.length === 0) return;

    const nextCompact = this.isCompact
      ? buttons.reduce((sum, button) => sum + button.getBoundingClientRect().width, 0)
        > track.clientWidth + 0.5
      : buttons.some((button) => this.isClipped(button));

    if (nextCompact === this.isCompact) return;

    const applyState = () => {
      this.isCompact = nextCompact;
      this.changeDetector.markForCheck();
      this.runAfterRender(() => this.updateScrollAffordance());
    };

    if (NgZone.isInAngularZone()) {
      applyState();
    } else {
      this.ngZone.run(applyState);
    }
  }

  /**
   * A grid-stretched tab never overflows its own box — flexbox just shrinks
   * its label to fit. The label's own
   * scrollWidth vs clientWidth is what actually reveals the clipping; the
   * button itself is checked too, for icon-only tabs whose fixed-size icon
   * has nothing left to shrink.
   */
  private isClipped(button: HTMLButtonElement): boolean {
    if (button.scrollWidth > button.clientWidth + 0.5) return true;
    return Array.from(button.querySelectorAll<HTMLElement>('.ds-tabs__label'))
      .some((label) => label.scrollWidth > label.clientWidth + 0.5);
  }

  private updateScrollAffordance(): void {
    const track = this.trackRef?.nativeElement;
    if (!track) return;

    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    const nextCanScrollStart = track.scrollLeft > 1;
    const nextCanScrollEnd = track.scrollLeft < maxScrollLeft - 1;
    if (nextCanScrollStart === this.canScrollStart && nextCanScrollEnd === this.canScrollEnd) return;

    const applyState = () => {
      this.canScrollStart = nextCanScrollStart;
      this.canScrollEnd = nextCanScrollEnd;
      this.changeDetector.markForCheck();
    };

    if (NgZone.isInAngularZone()) {
      applyState();
    } else {
      this.ngZone.run(applyState);
    }
  }
}
